import { ImportHtmlComponent } from '../import-html/import-html.component';
import { PAGE_SIZE_OPTIONS } from 'src/app/model/pagination-defaults';
import { SimpleConfirmDialogComponent } from '../../../../dialogs/simple-confirm-dialog/simple-confirm-dialog.component';
import { TranslocoService } from '@jsverse/transloco';
import { filter, firstValueFrom, forkJoin, Observable, skip, Subscription, take } from 'rxjs';
import { PackagingUnitNavigationService } from '../../../../../navigation/services/navigation-services/packaging-unit-navigation.service';
import { ActivatedRoute, ParamMap, Router, UrlTree } from '@angular/router';
import { Component, DestroyRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ImportExportApiService } from 'src/app/data-transfer/services/import-export-api-service';
import { ChangeType, ImportParentComponent } from '../import-parent/import-parent.component';
import { ChangePackagingUnitCandidateDto } from 'src/app/data-transfer/entities/packaging-unit-entities/change-packaging-unit-candidate-dto';
import { SimpleDialogData } from 'src/app/components/dialogs/simple-alert-dialog/simple-alert-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { getDialogConfig } from 'src/app/util/dialog-util';
import { AnalysisApiService } from 'src/app/data-transfer/services/analysis-api-service';
import { DialogActions } from 'src/app/model/dictionary';
import { PackagingPart } from 'src/app/model/packaging-part-enum';
import { DirectoryApiService } from 'src/app/data-transfer/services/directory-api-service';
import { Location } from '@angular/common';
import { ImportCandidateDto } from 'src/app/data-transfer/entities/import-candidate-dto';
import { ImportCandidatePaginationFields } from 'src/app/data-transfer/entities/pagination-fields';
import { DeleteDialogData, DeleteItemDialogComponent } from 'src/app/components/dialogs/delete-item-dialog/delete-item-dialog.component';
import { ChangeCandidateDto } from 'src/app/data-transfer/entities/change-candidate-dto';
import { FormControl } from '@angular/forms';
import { ComponentNavigationService } from 'src/app/navigation/services/navigation-services/component-navigation-service';
import { ComponentTypeService } from 'src/app/navigation/services/component-type-service';
import { MaterialNavigationService } from 'src/app/navigation/services/navigation-services/material-navigation.service';
import { PackagingSystemNavigationService } from 'src/app/navigation/services/navigation-services/packaging-system-navigation.service';
import {
  ArchiveActivatedText,
  ArchiveText,
  ChangeTrackingText, ComponentText,
  ImportText,
  ImportTypeText,
  MaterialText,
  NewDataSetsText,
  PackagingSystemText,
  PackagingTypeText,
  PackagingUnitText,
} from 'src/app/model/path-building-blocks';
import { ImportTypeSetting, PackagingImportHelper } from '../../../models/packaging-import-helper';
import { PackagingUnitImportHelper } from '../../../models/packaging-unit-import-helper';
import { PackagingComponentImportHelper } from '../../../models/packaging-component-import-helper';
import { PackagingMaterialImportHelper } from '../../../models/packaging-material-import-helper';
import { PackagingSystemImportHelper } from '../../../models/packaging-system-import-helper';

@Component({
  selector: 'app-import-packaging',
  templateUrl: './import-packaging.component.html',
  styleUrls: ['./import-packaging.component.scss'],
})
export class ImportPackagingComponent extends ImportParentComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('importHtml') importHtmlComponent!: ImportHtmlComponent;
  @ViewChild('changeHtml') changeHtmlComponent!: ImportHtmlComponent;

  protected readonly pageSizeOptions = PAGE_SIZE_OPTIONS.DEFAULT;
  protected currentPageSize = this.pageSizeOptions[0];

  selectedTabIndex = 0;
  packagingPartEnum = PackagingPart;
  packagingPartControl = new FormControl(PackagingPart.Unit, { nonNullable: true });
  archiveControl = new FormControl(false, { nonNullable: true })
  packagingPartImportHelper!: PackagingImportHelper;
  selectedTabsSeen:number[] = [];

  private loadedTabIndices: number[] = [];

  selectedCandidateIds: Set<number> = new Set<number>();
  selectedChanges: ChangeCandidateDto[] = [];

  private packagingPartControlSubscribtion?: Subscription
  private archiveControlSubscribtion?: Subscription
  private paramSubscribtion?: Subscription

  get selectedPackagingPart(): PackagingPart {
    return this.packagingPartControl.value;
  }

  get tableTitleImports(): string {
    return this.translateService.translate(this.packagingPartImportHelper.getTableTitle(ImportTypeSetting.Import, this.archiveActive));
  }

  get tableTitleChanges(): string {
    return this.translateService.translate(this.packagingPartImportHelper.getTableTitle(ImportTypeSetting.ChangeTracking, this.archiveActive));
  }

  get displayedColumns() {
    return this.packagingPartImportHelper.getDisplayedColumns();
  }

  constructor(
    protected dialog: MatDialog,
    protected translateService: TranslocoService,
    protected directoryApiService: DirectoryApiService,
    private importExportApiService: ImportExportApiService,
    private analysisApiService: AnalysisApiService,
    private packagingUnitNavigationService: PackagingUnitNavigationService,
    private componentNavigationService: ComponentNavigationService,
    private componentTypeService: ComponentTypeService,
    private materialNavigationService: MaterialNavigationService,
    private packagingSystemNavigationService: PackagingSystemNavigationService,
    private spinner: NgxSpinnerService,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location
  ) {
    super(dialog, translateService, directoryApiService);
  }

  ngOnInit(): void {
    this.packagingPartControlSubscribtion = this.packagingPartControl.valueChanges.subscribe(part => {
      this.selectedTabsSeen = [];
      this.loadedTabIndices = [];
      this.changePackagingPart(part);
      this.loadDataForTab(this.selectedTabIndex);
    });
    this.archiveControlSubscribtion = this.archiveControl.valueChanges.subscribe(archiveFlag => {
      this.changeArchiveType(archiveFlag);
    });

    const initialPackagingPart = this.applyRouteParams(this.route.snapshot.paramMap);
    this.packagingPartControl.setValue(initialPackagingPart, { emitEvent: false });
    this.changePackagingPart(initialPackagingPart);
    this.loadDataForTab(this.selectedTabIndex);

    this.paramSubscribtion = this.route.paramMap.pipe(skip(1)).subscribe(params => {
      this.packagingPartControl.setValue(this.applyRouteParams(params));
    });
  }

  private applyRouteParams(params: ParamMap): PackagingPart {
    this.selectedTabIndex = params.get(ImportTypeText) === ChangeTrackingText ? 1 : 0;
    this.archiveControl.setValue(params.get(ArchiveActivatedText) === ArchiveText);

    const packagingPart = params.get(PackagingTypeText);
    if (packagingPart === ComponentText) {
      return PackagingPart.Component;
    }
    if (packagingPart === MaterialText) {
      return PackagingPart.Material;
    }
    if (packagingPart === PackagingSystemText) {
      return PackagingPart.System;
    }
    return PackagingPart.Unit;
  }

  override ngOnDestroy(): void {
    if (this.packagingPartControlSubscribtion) {
      this.packagingPartControlSubscribtion.unsubscribe();
    }
    if (this.archiveControlSubscribtion) {
      this.archiveControlSubscribtion.unsubscribe();
    }
    if(this.paramSubscribtion){
      this.paramSubscribtion.unsubscribe();
    }
    super.ngOnDestroy();
  }

  changeArchiveType(showArchive: boolean) {
    const archiveType = showArchive ? ArchiveActivatedText: NewDataSetsText;

    if (this.packagingPartImportHelper) {
      this.archiveActive = showArchive;
      this.packagingPartImportHelper.getImportCandidatesDataSource().addFilterOptions(ImportCandidatePaginationFields.IsImported, [showArchive]);
    }
    this.resetSelectedCandidateIds();
    const importType = this.selectedTabIndex === 0 ? ImportText : ChangeTrackingText;
    const packagingPart = this.getTextFromPart(this.packagingPartControl.getRawValue());
    const urlTree: UrlTree  = this.router.createUrlTree(['../../../', packagingPart, importType, archiveType], { relativeTo: this.route });
    this.location.replaceState(urlTree.toString());
  }

  changeImportType() {
    this.packagingPartControl.setValue(this.selectedPackagingPart, { emitEvent: false, onlySelf: true });
    this.updateUrl();
    this.loadDataForTab(this.selectedTabIndex);
    this.tabSeen();
  }

  changePackagingPart(packagingPart: PackagingPart) {
    if (packagingPart === PackagingPart.Unit) {
      this.packagingPartImportHelper = new PackagingUnitImportHelper(this.importExportApiService, this.packagingUnitNavigationService);
    }
    else if (packagingPart === PackagingPart.Component) {
      this.packagingPartImportHelper = new PackagingComponentImportHelper(this.importExportApiService, this.componentNavigationService, this.componentTypeService);
    }
    else if (packagingPart === PackagingPart.Material) {
      this.packagingPartImportHelper = new PackagingMaterialImportHelper(this.importExportApiService, this.materialNavigationService);
    }
    else if (packagingPart === PackagingPart.System) {
      this.packagingPartImportHelper = new PackagingSystemImportHelper(this.importExportApiService, this.packagingSystemNavigationService);
    }
    else {
      return
    }

    this.updateUrl();
  }

  private updateUrl() {
    const importType = this.selectedTabIndex === 0 ? ImportText : ChangeTrackingText;
    const archiveType = this.archiveActive ? ArchiveActivatedText: NewDataSetsText;
    const packagingPartText = this.getTextFromPart(this.packagingPartControl.getRawValue());
    const urlTree: UrlTree = this.router.createUrlTree(['../../../', packagingPartText, importType, archiveType], { relativeTo: this.route });
    this.location.replaceState(urlTree.toString());
  }

  private getTextFromPart (packagingPart: PackagingPart): string {
    switch(packagingPart) {
      case PackagingPart.System:
        return PackagingSystemText;
      case PackagingPart.Unit:
        return PackagingUnitText
      case PackagingPart.Component:
        return ComponentText
      case PackagingPart.Material:
        return MaterialText;
    }
  }

  private loadDataForTab(tabIndex: number): void {
    if (this.loadedTabIndices.includes(tabIndex)) {
      return;
    }
    this.loadedTabIndices.push(tabIndex);

    if (tabIndex === 0) {
      this.loadImportCandidates();
    } else {
      this.loadChangeCandidates();
    }
  }

  private loadImportCandidates() {
    setTimeout(() => {
      const dataSource = this.packagingPartImportHelper.getImportCandidatesDataSource();
      this.importHtmlComponent.setDataSource(dataSource, this.currentPageSize);

      dataSource.isLoading.pipe(filter(loading => !loading), take(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.resetSelectedCandidateIds();
        this.tabSeen();
      });

      dataSource.fetchData(0, this.currentPageSize, [
        { fieldName: ImportCandidatePaginationFields.IsImported, values: [this.archiveControl.value.toString()] },
      ]);
    }, 0);
  }

  setPageSize(pageSize: number) {
    this.currentPageSize = pageSize;
  }

  // TO-DO: Changes has no paginated backend endpoint yet, so this fetches the full, unpaginated
  // list via getPackagingChangeCandidates() and hands the client-side dataSourceChanges
  // MatTableDataSource to changeHtmlComponent.setDataSource(). Once a by-page endpoint exists:
  // fetch through a PaginationDataSource<ChangeCandidateDto> instead (mirroring
  // loadImportCandidates()'s use of getImportCandidatesDataSource()/dataSource.fetchData()), so
  // this method converges with loadImportCandidates() and can likely be merged into it.
  private loadChangeCandidates() {
    setTimeout(() => {
      this.spinner.show();
      const observableChanges = this.packagingPartImportHelper.getPackagingChangeCandidates();
      if (observableChanges == null) {
        this.spinner.hide();
        return;
      }

      this.loadDataSourceSubscription = observableChanges.subscribe(result => {
        this.dataSourceChanges.data = result;
        this.dataSourceChanges.data.sort(this.sortingForNotifications)
        this.changeHtmlComponent.setDataSource(this.dataSourceChanges);
        this.selectedChanges = [];
        this.spinner.hide();
        this.tabSeen();
      });
    }, 0);
  }

  tabSeen() {
    if(!this.selectedTabsSeen.includes(this.selectedTabIndex)) {
      this.selectedTabsSeen.push(this.selectedTabIndex);
      switch(this.selectedTabIndex) {
        case 0:
          this.packagingPartImportHelper.packagingImportCandidatesSeen().then(result => {
            this.packagingPartImportHelper.packagingImportCandidatesCountUpdate();
          })
          break;
        case 1:
          this.packagingPartImportHelper.packagingChangeCandidatesSeen().then(result => {
            this.packagingPartImportHelper.packagingChangeCandidatesCountUpdate();
          })
          break;
      }
    }
  }

  navigateToCandidate(candidate: ImportCandidateDto) {
    this.packagingPartImportHelper.navigateToCandidate(candidate);
  }

  navigateToChange(candidate: ChangeCandidateDto, seePackagingBeforeChange: boolean) {
    this.packagingPartImportHelper.navigateToChange(candidate, seePackagingBeforeChange);
  }

  // ---------- Import ----------

  setSelectedImportCandidateIds(candidateIds: Set<number>) {
    this.selectedCandidateIds = candidateIds;
  }

  removeCandidates() {
    const deleteFunction = this.packagingPartImportHelper.getDeleteFunction(ImportTypeSetting.Import);
    const dialogData: DeleteDialogData = {
      dialogHeader: this.translateService.translate('dataManagement.import.import.dialog.deleteHeader'),
      dialogText: this.translateService.translate('dataManagement.import.import.dialog.deleteText'),
    };
    const dialogRef = this.dialog.open(DeleteItemDialogComponent, getDialogConfig(dialogData, '500px'));
    this.dialogSubscription = dialogRef.afterClosed().subscribe(result => {
      if (result.event === DialogActions.DELETE) {
        const observables = [...this.selectedCandidateIds].map(candidateId => deleteFunction(candidateId));
        this.removeImportBackendSubscription = forkJoin(observables).subscribe(() => {
          this.packagingPartImportHelper.getImportCandidatesDataSource().refresh();
          this.resetSelectedCandidateIds();
        });
      }
    });
  }

  async proceedWithImport() {
    await super.selectDirectory();
    if (this.selectedImportType == null || this.selectedCandidateIds.size < 1 || this.targetDirectory == null) {
      return;
    }
    this.spinner.show();

    let recPossibleForAll = true;
    let lcaPossibleForAll = true;

    if (this.selectedPackagingPart === PackagingPart.Unit || this.selectedPackagingPart === PackagingPart.System) {
      recPossibleForAll = await this.isRecyclabilityAnalysisPossibleForSelected(ImportTypeSetting.Import);
      if (recPossibleForAll) {
        lcaPossibleForAll = await this.isLcaPossibleForSelected(ImportTypeSetting.Import);
      }
    }

    if (recPossibleForAll && lcaPossibleForAll) {
      this.doImport();
    } else {
      const data: SimpleDialogData = {
        title: this.translateService.translate('common.text.warning'),
        messages: [this.translateService.translate('warnings.importIncompletePackagingUnit')],
        icon: 'warning',
      };
      const confirmDialogConfig = getDialogConfig(data, '350px');
      const dialogRef = this.dialog.open(SimpleConfirmDialogComponent, confirmDialogConfig);
      this.dialogSubscription = dialogRef.afterClosed().subscribe(result => {
        if (result.event === DialogActions.CONFIRM) {
          this.doImport();
        } else {
          this.spinner.hide();
          return;
        }
      });
    }
  }

  private async doImport() {
    const importFunction = this.packagingPartImportHelper.getImportFunctionImportCandidate();
    await super.importItems([...this.selectedCandidateIds], importFunction);
    this.spinner.hide();
    this.resetSelectedCandidateIds();
    this.packagingPartImportHelper.packagingImportCandidatesCountUpdate();
    this.packagingPartImportHelper.getImportCandidatesDataSource().refresh();
  }

  private resetSelectedCandidateIds(): void {
    this.selectedCandidateIds = new Set<number>();
  }

  // ---------- Changes ----------

  setSelectedImportChanges(changes: ChangePackagingUnitCandidateDto[]) {
    this.selectedChanges = changes;
  }

  // TO-DO: routes through ImportParentComponent.removeChanges(), which deletes then writes the
  // remaining rows directly onto the client-side dataSourceChanges MatTableDataSource. Once
  // Changes has a paginated endpoint: stop calling super.removeChanges() and instead mirror
  // removeCandidates() above - delete via deleteFunction, then call the change-tracking
  // PaginationDataSource's refresh() instead of mutating dataSourceChanges.
  removeChanges() {
    const deleteFunction = this.packagingPartImportHelper.getDeleteFunction(ImportTypeSetting.ChangeTracking);
    const idsToDelete: number[] = this.packagingPartImportHelper.getPackagingIdsFromChangeCandidates(this.selectedChanges);
    super.removeChanges(this.selectedChanges, deleteFunction, idsToDelete, this.changeHtmlComponent);
  }

  async proceedWithChange() {
    let targetDirectoryRequired = false;
    if ([ChangeType.WithTrackingNew, ChangeType.WithoutTracking].includes(this.selectedChangeType)) {
      targetDirectoryRequired = true;
      await super.selectDirectory();
    }
    if (this.selectedChangeType == null || this.selectedChanges.length < 1 || (this.targetDirectory == null && targetDirectoryRequired)) {
      return;
    }
    this.spinner.show();
    let recPossibleForAll = true;
    let lcaPossibleForAll = true;

    if (this.selectedPackagingPart === PackagingPart.Unit || this.selectedPackagingPart === PackagingPart.System) {
      recPossibleForAll = await this.isRecyclabilityAnalysisPossibleForSelected(ImportTypeSetting.ChangeTracking);
      if (recPossibleForAll) {
        lcaPossibleForAll = await this.isLcaPossibleForSelected(ImportTypeSetting.ChangeTracking);
      }
    }

    if (recPossibleForAll && lcaPossibleForAll) {
      this.doChange();
    } else {
      const data: SimpleDialogData = {
        title: this.translateService.translate('common.text.warning'),
        messages: [this.translateService.translate('warnings.importIncompletePackagingUnit')],
        icon: 'warning',
      };
      const confirmDialogConfig = getDialogConfig(data, '350px');
      const dialogRef = this.dialog.open(SimpleConfirmDialogComponent, confirmDialogConfig);
      this.dialogSubscription = dialogRef.afterClosed().subscribe(result => {
        if (result.event === DialogActions.CONFIRM) {
          this.doChange();
        } else {
          this.spinner.hide();
          return;
        }
      });
    }
  }

  private async doChange() {
    if (this.selectedChangeType === this.changeTypeEnum.WithTrackingOverwrite) {
      const changeFunction = this.packagingPartImportHelper.getChangeFunctionChangeCandidate();

      const idsToChange: number[] = this.packagingPartImportHelper.getPackagingIdsFromChangeCandidates(this.selectedChanges);
      await super.applyChangesExistingItems(idsToChange, changeFunction);
      this.selectedChanges = [];
    } else {
      const isWithTracking = this.selectedChangeType === this.changeTypeEnum.WithTrackingNew;
      const changeFunction = this.packagingPartImportHelper.getImportFunctionImportCandidate();

      const idsToChange: number[] = this.selectedChanges.map(x => x.importCandidateId);
      await super.applyChangesNewItems(idsToChange, isWithTracking, changeFunction);
      this.selectedChanges = [];
    }
    this.spinner.hide();
    this.packagingPartImportHelper.packagingChangeCandidatesCountUpdate();
    this.loadChangeCandidates();
  }

  private async isRecyclabilityAnalysisPossibleForSelected(importType: ImportTypeSetting): Promise<boolean> {
    let observablesRec: Observable<boolean>[] = [];
    if (importType === ImportTypeSetting.Import) {
      observablesRec = [...this.selectedCandidateIds].map(candidateId => this.analysisApiService.isRecyclabilityAnalysisPossibleImport(candidateId, this.selectedPackagingPart));
    }
    if (importType === ImportTypeSetting.ChangeTracking) {
      observablesRec = this.selectedChanges.map(change => this.analysisApiService.isRecyclabilityAnalysisPossibleImport(change.importCandidateId, this.selectedPackagingPart));
    }

    let isRecPossible: boolean[] = [];
    if (observablesRec.length > 0) {
      isRecPossible = await firstValueFrom(forkJoin(observablesRec));
    }
    const recPossibleForAll = !isRecPossible.includes(false);
    return recPossibleForAll;
  }

  private async isLcaPossibleForSelected(importType: ImportTypeSetting): Promise<boolean> {
    let observablesLca: Observable<boolean>[] = [];
    if (importType === ImportTypeSetting.Import) {
      observablesLca = [...this.selectedCandidateIds].map(candidateId => this.analysisApiService.isLifeCycleAnalysisPossibleImport(candidateId, this.selectedPackagingPart));
    }
    if (importType === ImportTypeSetting.ChangeTracking) {
      observablesLca = this.selectedChanges.map(change => this.analysisApiService.isLifeCycleAnalysisPossibleImport(change.importCandidateId, this.selectedPackagingPart));
    }

    let isLcaPossible: boolean[] = [];
    if (observablesLca.length > 0) {
      isLcaPossible = await firstValueFrom(forkJoin(observablesLca));
    }
    const recPossibleForAll = !isLcaPossible.includes(false);
    return recPossibleForAll;
  }
}
